import { useMemo } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  Label,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { Text } from '@buffer-mono/popcorn'

import { ChartTooltip } from './ChartTooltip'
import {
  resolveLightColor,
  useIsSnapshotChartTheme,
} from '../charts/snapshotChartTheme'
import { makeYAxisTickFormatter } from './formatYAxisTick'
import { useDocumentColorScheme } from './useDocumentColorScheme'
import {
  axisTickLabels,
  computeYAxisWidth,
  makeFormatDateTick,
  makeMonthTickFormatter,
  makeWeekTickFormatter,
  resolveColor,
  TICK_FONT_SIZE,
  Y_AXIS_LABEL_OFFSET,
  Y_AXIS_TICK_MARGIN,
  Y_AXIS_WIDTH,
  Y_TICK_COUNT,
} from './chartPrimitives'
import {
  buildBarChartData,
  computeBarYDomain,
  makeBucketTooltipFormatter,
  monthsInRange,
  pickBarTimeGranularity,
} from './barChartData'
import type { BarChartSeries } from './barChartData'
import { bucketTimeSeries } from './bucketTimeSeries'
import { SummaryMode } from './types'
import type { MetricSeries } from './types'

import styles from './BarChartBase.module.css'

const BAR_TOP_RADIUS: [number, number, number, number] = [2, 2, 0, 0]
const X_AXIS_PADDING = 20

export type { BarChartSeries }

export interface BarChartBaseProps {
  series: BarChartSeries[]
  /**
   * Stack every series into one bar per period (true) or draw them as grouped
   * columns side by side (false). The only behavioural difference between the
   * `StackedBarChart` and `BarChart` primitives.
   */
  stacked: boolean
  /** Range bounds — only used to decide whether ticks need a year. */
  startDate?: Date
  endDate?: Date
  /** Left-axis label (e.g. the metric name). */
  yAxisLabel?: string
  height?: number
  /** Month-only date labels (no day) for monthly-bucketed data. */
  monthly?: boolean
  /** Show each series' icon in the tooltip rows. */
  showIcons?: boolean
  /** Content shown when there's nothing to plot; axes/grid stay visible. */
  emptyState?: ReactNode
  /** Accessible name for the chart; distinguishes multiple charts on a page. */
  ariaLabel?: string
  /** Disable enter animations (e.g. for a static share snapshot). */
  disableAnimations?: boolean
}

/**
 * Shared engine for the `BarChart` (grouped) and `StackedBarChart` primitives —
 * a recharts bar chart over day-resolution series with the Insights axis, grid,
 * and tooltip styling. Not exported directly; consumers use the two typed
 * wrappers so the chart type is explicit at the call site.
 *
 * The tooltip reuses the shared `ChartTooltip`; series are adapted to its
 * `MetricSeries` shape with `Total` summary mode, so a hovered day with no bar
 * reads as "no post" rather than carrying a value forward.
 */
export function BarChartBase({
  series,
  stacked,
  startDate,
  endDate,
  yAxisLabel,
  height = 250,
  monthly = false,
  showIcons = false,
  emptyState,
  ariaLabel = 'Bar chart',
  disableAnimations = false,
}: BarChartBaseProps): JSX.Element {
  // Colours and axis tokens are resolved from CSS custom properties at render
  // time (recharts can't read them); `colorScheme` is a memo dependency so they
  // re-resolve when the light/dark class on <html> flips. @see useDocumentColorScheme
  const colorScheme = useDocumentColorScheme()
  // The offscreen Share-as-Post snapshot renders on a fixed light card, so it
  // resolves the light-theme palette regardless of the app's current theme, and
  // renders both axes' tick labels in the subtle text token (design: dates +
  // amounts both use text/subtle). @see snapshotChartTheme
  const isSnapshot = useIsSnapshotChartTheme()

  const granularity =
    startDate && endDate
      ? pickBarTimeGranularity(startDate, endDate)
      : undefined

  // Sum each series into buckets of that granularity so a wide range renders
  // one bar per bucket instead of a field of thin daily bars.
  const bucketedSeries = useMemo(
    () =>
      granularity
        ? series.map((s) => ({
            ...s,
            data: bucketTimeSeries(s.data, granularity),
          }))
        : series,
    [series, granularity],
  )

  // Colours and axis tokens are resolved from CSS custom properties at render
  // time (recharts can't read them); `colorScheme` is a memo dependency so they
  // re-resolve when the light/dark class on <html> flips. @see useDocumentColorScheme
  const { data, resolvedColors, gridColor, neutralTextColor, subtleTextColor } =
    useMemo(() => {
      const resolve = isSnapshot ? resolveLightColor : resolveColor
      const resolved: Record<string, string> = {}
      for (const s of bucketedSeries) resolved[s.key] = resolve(s.color)
      const subtle = resolve('var(--color-text-subtle)')
      return {
        data: buildBarChartData(bucketedSeries),
        resolvedColors: resolved,
        gridColor: resolve('var(--color-border-subtle)'),
        neutralTextColor: isSnapshot
          ? subtle
          : resolve('var(--color-text-neutral)'),
        subtleTextColor: subtle,
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bucketedSeries, colorScheme, isSnapshot])

  const yDomain = useMemo(
    () => computeBarYDomain(data, bucketedSeries, stacked, Boolean(emptyState)),
    [data, bucketedSeries, stacked, emptyState],
  )

  // Size the band to its own tick labels so the numbers stay a fixed gap from
  // the rotated axis label whatever their width (see computeYAxisWidth).
  const axisWidth = useMemo(
    () =>
      emptyState
        ? Y_AXIS_WIDTH
        : computeYAxisWidth(
            axisTickLabels(yDomain, makeYAxisTickFormatter(yDomain)),
          ),
    [emptyState, yDomain],
  )

  const monthLabels = granularity === 'month' || monthly
  const compactMonthLabels =
    granularity === 'month' &&
    startDate != null &&
    endDate != null &&
    monthsInRange(startDate, endDate) > 6
  const formatDateTick = useMemo(() => {
    if (granularity === 'week') return makeWeekTickFormatter()
    if (granularity === 'month')
      return makeMonthTickFormatter(compactMonthLabels)
    return makeFormatDateTick(startDate, endDate, monthly)
  }, [granularity, compactMonthLabels, startDate, endDate, monthly])

  // The tooltip header names the bucket's whole period (a week range, a month,
  // or a single day) rather than just its start day. Only meaningful once
  // bucketed (a range is present).
  const formatTooltipDate = useMemo(
    () => (granularity ? makeBucketTooltipFormatter(granularity) : undefined),
    [granularity],
  )

  // ChartTooltip reads `MetricSeries`; adapt with Total summary mode so gaps
  // resolve exact-day (no carry-forward) — matching how the bars fill.
  const tooltipMetrics = useMemo<MetricSeries[]>(
    () =>
      bucketedSeries.map((s) => ({
        key: s.key,
        title: s.label,
        icon: s.icon ?? null,
        color: s.color,
        summaryMode: SummaryMode.Total,
        data: s.data.map((point) => ({ date: point.date, value: point.value })),
      })),
    [bucketedSeries],
  )

  return (
    <div className={styles.chartRoot}>
      <ResponsiveContainer width="100%" height={height}>
        <RechartsBarChart data={data} aria-label={ariaLabel}>
          <CartesianGrid vertical={false} stroke={gridColor} />

          <XAxis
            dataKey="timestamp"
            type="category"
            interval="preserveStartEnd"
            tickFormatter={formatDateTick}
            tick={{ fontSize: TICK_FONT_SIZE, fill: neutralTextColor, dy: 8 }}
            axisLine={{ stroke: gridColor }}
            tickLine={false}
            padding={{ left: X_AXIS_PADDING, right: X_AXIS_PADDING }}
          />

          <YAxis
            domain={yDomain}
            width={axisWidth}
            tick={{ fontSize: TICK_FONT_SIZE, fill: subtleTextColor }}
            axisLine={false}
            tickLine={false}
            tickSize={0}
            tickMargin={Y_AXIS_TICK_MARGIN}
            tickCount={Y_TICK_COUNT}
            // Counts are whole numbers — keep recharts from inserting fractional
            // ticks (e.g. 0.75, 1.5) on small ranges.
            allowDecimals={false}
            tickFormatter={
              emptyState ? undefined : makeYAxisTickFormatter(yDomain)
            }
          >
            {yAxisLabel && (
              <Label
                value={yAxisLabel}
                angle={-90}
                position="insideLeft"
                offset={Y_AXIS_LABEL_OFFSET}
                style={{
                  fill: subtleTextColor,
                  fontSize: TICK_FONT_SIZE,
                  textAnchor: 'middle',
                }}
              />
            )}
          </YAxis>

          <Tooltip
            content={(props) => (
              <ChartTooltip
                {...props}
                metrics={tooltipMetrics}
                showIcons={showIcons}
                monthly={monthLabels}
                formatDate={formatTooltipDate}
              />
            )}
            cursor={{ fill: 'transparent' }}
          />

          {bucketedSeries.map((s, index) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              stackId={stacked ? 'stack' : undefined}
              fill={resolvedColors[s.key]}
              // Round only the bar's top: the last segment of a stack, or every
              // grouped column's own top.
              radius={
                stacked
                  ? index === bucketedSeries.length - 1
                    ? BAR_TOP_RADIUS
                    : undefined
                  : BAR_TOP_RADIUS
              }
              isAnimationActive={!disableAnimations}
            />
          ))}
        </RechartsBarChart>
      </ResponsiveContainer>
      {emptyState && (
        <div className={styles.chartEmptyOverlay}>
          {typeof emptyState === 'string' ? (
            <Text size="sm" color="subtle">
              {emptyState}
            </Text>
          ) : (
            emptyState
          )}
        </div>
      )}
    </div>
  )
}
